import { useEffect, useState } from "react"
import dayjs from "dayjs"
import { Flex, Form } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import { useWizardContext } from "../WizardContext"
import { buildPricingPayload, buildPrivatePricingPayload, isServerId } from "../mappers/toApiPayload"
import BasePrices from "../components/pricing/BasePrices"
import AdjustmentsSection from "../components/pricing/AdjustmentsSection"
import PaymentType from "../components/pricing/PaymentType"
import TaxesAndFees from "../components/pricing/TaxesAndFees"
import AddDayAdjustmentModal, { APPLIES_ON } from "../components/pricing/AddDayAdjustmentModal"
import AddSeasonalAdjustmentModal from "../components/pricing/AddSeasonalAdjustmentModal"
import AddPrivateSeasonalAdjustmentModal from "../components/pricing/AddPrivateSeasonalAdjustmentModal"
import { PrivateBookingOptions } from "../components/pricing/PrivateBookingOptions/PrivateBookingOptions"
import { MultiDayPricingModel } from "../components/pricing/MultiDayPricingModel/MultiDayPricingModel"
import { GuestDetails } from "../components/scheduling/GuestDetails/GuestDetails"
import { MULTI_DAY_PRICE_ALLOWED, PRICING_TYPES, TRIP_PRICE } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { AFFECTS, APPLIES_ON_PRIVATE, PRICE_DIRECTION, PRICE_TYPE } from "../constants/pricingConstants"
import { DURATION_TYPES } from "../components/scheduling/ExperienceDurationType/ExperienceDurationType"
import styles from "./PricingStep.module.css"

const formatRateChange = ({ direction, priceValue, priceType }) => {
  const sign = direction === PRICE_DIRECTION.INCREASE ? "+" : "-"
  // Currency goes before the amount (+$10); percentage stays after (+10%).
  if (priceType === PRICE_TYPE.PERCENT) return `${sign}${priceValue}%`
  return `${sign}$${priceValue}`
}

export const PricingStep = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const isTicketed = searchParams.get("type") === "ticketed"
  const { form, stepSaveHandlersRef, stepValidateHandlersRef, trip } = useWizardContext()

  const watchedTypes = Form.useWatch("ticket_types", form)
  const ticketTypeOptions = ((watchedTypes?.length ? watchedTypes : trip?.ticket_types) || []).map((t) => ({
    id: t.id,
    label: t.title,
  }))

  const durationTypeWatched = Form.useWatch("duration_type", form)
  const durationType = durationTypeWatched ?? form.getFieldValue("duration_type")
  const isMultiDay = !isTicketed && durationType === DURATION_TYPES.MULTI_DAY

  // Seasonal scheduling drives the private seasonal adjustment:
  //  - All Year (not seasonal) → hide the Seasonal Adjustments section entirely.
  //  - "Default Schedule" adjustments reuse the season date ranges set in Scheduling.
  const isSeasonal = Form.useWatch("seasonal", form) ?? form.getFieldValue("seasonal")
  const seasonTimeBlocks = Form.useWatch("timeBlocks", form) || form.getFieldValue("timeBlocks") || []
  const defaultScheduleRanges = (seasonTimeBlocks || [])
    .map((blockIndex) => {
      const start = form.getFieldValue(`seasonStart_${blockIndex}`)
      const end = form.getFieldValue(`seasonEnd_${blockIndex}`)
      return start && end ? { id: blockIndex, value: [start, end] } : null
    })
    .filter(Boolean)

  const formatAffectsDisplay = ({ affects, selectedTickets }) => {
    if (affects === AFFECTS.ALL) return t("experiences.pricing.adjustment_modal.affects_all_display")
    return ticketTypeOptions.filter((ticket) => selectedTickets.includes(ticket.id))
      .map((ticket) => ticket.label)
      .join(", ")
  }

  const formatAppliesOnDisplay = ({ appliesOn, customDays }) => {
    if (appliesOn === APPLIES_ON.WEEKDAYS) return t("experiences.pricing.day_adjustment_modal.applies_on_weekdays")
    if (appliesOn === APPLIES_ON.WEEKEND) return t("experiences.pricing.day_adjustment_modal.applies_on_weekend")
    return (customDays || []).join(", ")
  }

  const formatSeasonalAppliesOnDisplay = ({ startDate, endDate, isRepeatEachYear }) => {
    const start = startDate ? startDate.format("MMM D") : ""
    const end = endDate ? endDate.format("MMM D") : ""
    const range = `${start} - ${end}`
    return isRepeatEachYear
      ? `${range} • ${t("experiences.pricing.seasonal_adjustment_modal.repeats_yearly")}`
      : range
  }

  const formatPrivateSeasonalAppliesOnDisplay = ({ appliesOn, isRepeatEachYear }) => {
    const labels = {
      [APPLIES_ON_PRIVATE.DEFAULT_SCHEDULE]: t("experiences.pricing.seasonal_adjustment_modal.applies_on_default_schedule"),
      [APPLIES_ON_PRIVATE.HOLIDAYS]: t("experiences.pricing.seasonal_adjustment_modal.applies_on_holidays"),
      [APPLIES_ON_PRIVATE.CUSTOM_DATES]: t("experiences.pricing.seasonal_adjustment_modal.applies_on_custom_dates"),
    }
    const label = labels[appliesOn] || appliesOn
    return isRepeatEachYear ? `${label} • ${t("experiences.pricing.seasonal_adjustment_modal.repeats_yearly")}` : label
  }

  // useState drives rendering; form.setFieldValue keeps data in the shared form for the save handler
  const [isDayModalOpen, setIsDayModalOpen] = useState(false)
  const [isSeasonalModalOpen, setIsSeasonalModalOpen] = useState(false)
  const [editingDayItem, setEditingDayItem] = useState(null)
  const [editingSeasonalItem, setEditingSeasonalItem] = useState(null)
  const [dayAdjustments, setDayAdjustments] = useState(() => form.getFieldValue("dayAdjustments") || [])
  const [seasonalAdjustments, setSeasonalAdjustments] = useState(() => form.getFieldValue("seasonalAdjustments") || [])
  const [fees, setFees] = useState(() => form.getFieldValue("pricingFees") || [])

  // Keep form in sync so the save handler can read these fields
  useEffect(() => { form.setFieldValue("dayAdjustments", dayAdjustments) }, [dayAdjustments])
  useEffect(() => { form.setFieldValue("seasonalAdjustments", seasonalAdjustments) }, [seasonalAdjustments])
  useEffect(() => { form.setFieldValue("pricingFees", fees) }, [fees])

  // Seed from Redux when form wasn't initialized with adjustment data (WizardProvider race condition).
  useEffect(() => {
    if (dayAdjustments.length > 0 || !trip?.day_adjustments?.length) return
    setDayAdjustments(
      trip.day_adjustments.map((a) => {
        const mapped = {
          id: a.id,
          name: a.name,
          appliesOn: a.applies_on,
          customDays: (a.custom_days || []).filter(Boolean),
          direction: a.direction,
          priceValue: parseFloat(a.price_value),
          priceType: a.price_type,
          affects: a.affects,
          selectedTickets: a.selected_ticket_ids || [],
        }
        return {
          ...mapped,
          rateChange: formatRateChange(mapped),
          affectsDisplay: formatAffectsDisplay(mapped),
          appliesOnDisplay: formatAppliesOnDisplay(mapped),
        }
      })
    )
  }, [trip?.day_adjustments])

  useEffect(() => {
    if (fees.length > 0 || !trip?.assigned_custom_fees?.length) return
    // Fees with a null id are system/platform fees (e.g. Platform Fee, Card
    // Processing Fee) — they aren't guide-managed custom fees, so don't show them.
    setFees(trip.assigned_custom_fees.filter((fee) => fee.id != null))
  }, [trip?.assigned_custom_fees])

  useEffect(() => {
    if (seasonalAdjustments.length > 0 || !trip?.seasonal_adjustments?.length) return
    setSeasonalAdjustments(
      trip.seasonal_adjustments.map((a) => {
        const mapped = {
          id: a.id,
          name: a.name,
          // Dates live in date_ranges[0] (nested) — fall back to top-level fields for older records.
          startDate: a.date_ranges?.[0]?.start_date
            ? dayjs(a.date_ranges[0].start_date)
            : a.start_date ? dayjs(a.start_date) : null,
          endDate: a.date_ranges?.[0]?.end_date
            ? dayjs(a.date_ranges[0].end_date)
            : a.end_date ? dayjs(a.end_date) : null,
          // Private seasonal model: applies_on_schedule_type + nested date_ranges
          // (the modal reads appliesOn + dateRanges; ticketed reads startDate/endDate).
          appliesOn: a.applies_on_schedule_type ?? a.applies_on,
          dateRanges: (a.date_ranges || []).map((dateRange) => ({
            id: dateRange.id,
            value: [dayjs(dateRange.start_date), dayjs(dateRange.end_date)],
          })),
          direction: a.direction,
          priceValue: parseFloat(a.price_value),
          priceType: a.price_type,
          affects: a.affects,
          selectedTickets: a.selected_ticket_ids || [],
          isRepeatEachYear: a.is_repeat_each_year ?? true,
        }
        return {
          ...mapped,
          rateChange: formatRateChange(mapped),
          affectsDisplay: formatAffectsDisplay(mapped),
          appliesOnDisplay: isTicketed
            ? formatSeasonalAppliesOnDisplay(mapped)
            : formatPrivateSeasonalAppliesOnDisplay(mapped),
        }
      })
    )
  }, [trip?.seasonal_adjustments])

  useEffect(() => {
    stepValidateHandlersRef.current["pricing"] = () => {
      if (isTicketed) {
        const ticketTypes = form.getFieldValue("ticket_types") || []
        const ticketTypePrices = form.getFieldValue("ticketTypePrices") || {}
        const activeTicketTypes = ticketTypes.filter((ticket) => !ticket._destroy)
        const hasNoTypes = activeTicketTypes.length === 0
        const hasMissingPrice = activeTicketTypes.some((ticket) => {
          const priceEntry = ticketTypePrices[ticket.id]
          if (!priceEntry) return true
          if (priceEntry.enabled === false) return false
          return priceEntry.price == null || priceEntry.price <= 0
        })
        if (hasNoTypes || hasMissingPrice) {
          const err = new Error(t("experiences.pricing.base_prices.price_required"))
          err.errorFields = [{ errors: [t("experiences.pricing.base_prices.price_required")] }]
          throw err
        }
        return
      }

      // Private per-person / flat-rate: at least one booking option is required.
      // Multi-day uses a single flat price model instead, so it's exempt.
      if (!isMultiDay) {
        // Deleted options linger in localPrices flagged _destroy — they must not
        // satisfy the requirement.
        const localPrices = (form.getFieldValue("localPrices") || []).filter((price) => !price._destroy)
        if (localPrices.length === 0) {
          const msg = t("experiences.pricing.booking_options.required", { defaultValue: "Add at least one booking option" })
          const err = new Error(msg)
          err.errorFields = [{ errors: [msg] }]
          throw err
        }
        return
      }

      // Multi-day flat-rate: guests_included and flat_rate have required Form.Item rules
      // but form.validateFields() is never called with those field names during step
      // navigation — they're not in stepFields["pricing"]. Trigger validation explicitly.
      const pricingType = form.getFieldValue("pricing_type") || PRICING_TYPES.PER_PERSON
      if (pricingType === PRICING_TYPES.FLAT_RATE) {
        const maxGuests = form.getFieldValue("max_guests")
        const guestsIncluded = form.getFieldValue(TRIP_PRICE.GUESTS_INCLUDED)
        const notIncluded =
          typeof maxGuests === "number" && typeof guestsIncluded === "number"
            ? Math.max(0, maxGuests - guestsIncluded)
            : null
        const fieldsToValidate = [TRIP_PRICE.GUESTS_INCLUDED, TRIP_PRICE.FLAT_RATE]
        if (notIncluded > 0) fieldsToValidate.push(TRIP_PRICE.PRICE_ADULT)
        return form.validateFields(fieldsToValidate)
      }

      // Multi-day per-person: at least one enabled guest type must have a price > 0.
      const enabledPricePairs = [
        { allowed: MULTI_DAY_PRICE_ALLOWED.ADULTS, price: TRIP_PRICE.PRICE_ADULT },
        { allowed: MULTI_DAY_PRICE_ALLOWED.CHILDREN, price: TRIP_PRICE.PRICE_CHILD },
        { allowed: MULTI_DAY_PRICE_ALLOWED.INFANTS, price: TRIP_PRICE.PRICE_INFANT },
        { allowed: MULTI_DAY_PRICE_ALLOWED.PASSENGERS, price: TRIP_PRICE.PRICE_PASSENGER },
      ]
      const hasAtLeastOnePrice = enabledPricePairs.some(
        ({ allowed, price }) => form.getFieldValue(allowed) && Number(form.getFieldValue(price)) > 0
      )
      if (!hasAtLeastOnePrice) {
        const msg = t("experiences.pricing.private.at_least_one_price_required")
        const err = new Error(msg)
        err.errorFields = [{ errors: [msg] }]
        throw err
      }
    }
    return () => {
      delete stepValidateHandlersRef.current["pricing"]
    }
  }, [stepValidateHandlersRef, form, t, isTicketed, isMultiDay])

  useEffect(() => {
    if (isTicketed) {
      stepSaveHandlersRef.current["pricing"] = async () => {
        const formValues = form.getFieldsValue()
        const dayAdjustments = form.getFieldValue("dayAdjustments") || []
        const seasonalAdjustments = form.getFieldValue("seasonalAdjustments") || []
        const pricingFees = form.getFieldValue("pricingFees") || []
        const ticketTypePrices = form.getFieldValue("ticketTypePrices") || {}
        return buildPricingPayload({ ...formValues, dayAdjustments, seasonalAdjustments, pricingFees, ticketTypePrices })
      }
    } else {
      stepSaveHandlersRef.current["pricing"] = async () => {
        const localPrices = form.getFieldValue("localPrices") || []
        const serverPriceIds = (trip?.trip_prices || []).map((p) => p.id)
        const dayAdjustments = form.getFieldValue("dayAdjustments") || []
        const seasonalAdjustments = form.getFieldValue("seasonalAdjustments") || []
        const pricingFees = form.getFieldValue("pricingFees") || []
        // getFieldsValue(true) returns ALL store values, not just fields registered on
        // the Pricing step. The payload builder reads cross-step fields — duration_type
        // (Scheduling) decides the multi-day vs single-day branch, and licenses_required
        // / licenses_list live on Details. With the default getFieldsValue() those are
        // omitted, so duration_type was undefined and multi-day fell through to the
        // single-day branch (emitting trip_duration + stale localPrices allowed flags).
        const formValues = form.getFieldsValue(true)
        // Multi-day guest-type flags live under dedicated MULTI_DAY_PRICE_ALLOWED keys
        // (set via setFieldValue, no Form.Item) to avoid colliding with trip-level
        // fields on the shared form — notably the Details "child friendly" radio is
        // name="children_allowed", which would otherwise force children_allowed: true.
        // Read each here and map to the backend keys the payload builder expects.
        const allowedOverrides = {
          adults_allowed: form.getFieldValue(MULTI_DAY_PRICE_ALLOWED.ADULTS) ?? true,
          children_allowed: form.getFieldValue(MULTI_DAY_PRICE_ALLOWED.CHILDREN) ?? false,
          infants_allowed: form.getFieldValue(MULTI_DAY_PRICE_ALLOWED.INFANTS) ?? false,
          passengers_allowed: form.getFieldValue(MULTI_DAY_PRICE_ALLOWED.PASSENGERS) ?? false,
        }
        return buildPrivatePricingPayload({ localPrices, serverPriceIds, dayAdjustments, seasonalAdjustments, pricingFees, formValues: { ...formValues, ...allowedOverrides } })
      }
    }
    return () => {
      delete stepSaveHandlersRef.current["pricing"]
    }
  }, [stepSaveHandlersRef, form, isTicketed, trip?.trip_prices])

  const handleDayAdjustmentSave = (values) => {
    const formatted = {
      ...values,
      rateChange: formatRateChange(values),
      affectsDisplay: formatAffectsDisplay(values),
      appliesOnDisplay: formatAppliesOnDisplay(values),
    }
    if (values.id) {
      setDayAdjustments((prev) => prev.map((a) => (a.id === values.id ? { ...a, ...formatted } : a)))
    } else {
      setDayAdjustments((prev) => [...prev, { id: Date.now(), ...formatted }])
    }
    setIsDayModalOpen(false)
    setEditingDayItem(null)
  }

  const handleSeasonalAdjustmentSave = (values) => {
    const formatted = {
      ...values,
      rateChange: formatRateChange(values),
      affectsDisplay: formatAffectsDisplay(values),
      appliesOnDisplay: formatSeasonalAppliesOnDisplay(values),
    }
    if (values.id) {
      setSeasonalAdjustments((prev) => prev.map((a) => (a.id === values.id ? { ...a, ...formatted } : a)))
    } else {
      setSeasonalAdjustments((prev) => [...prev, { id: Date.now(), ...formatted }])
    }
    setIsSeasonalModalOpen(false)
    setEditingSeasonalItem(null)
  }

  const handlePrivateSeasonalAdjustmentSave = (values) => {
    const formatted = {
      ...values,
      rateChange: formatRateChange(values),
      affectsDisplay: "",
      appliesOnDisplay: formatPrivateSeasonalAppliesOnDisplay(values),
    }
    if (values.id) {
      setSeasonalAdjustments((prev) => prev.map((a) => (a.id === values.id ? { ...a, ...formatted } : a)))
    } else {
      setSeasonalAdjustments((prev) => [...prev, { id: Date.now(), ...formatted }])
    }
    setIsSeasonalModalOpen(false)
    setEditingSeasonalItem(null)
  }

  const handleDayEdit = (item) => {
    setEditingDayItem(item)
    setIsDayModalOpen(true)
  }

  const handleSeasonalEdit = (item) => {
    setEditingSeasonalItem(item)
    setIsSeasonalModalOpen(true)
  }

  const handleFeesAdd = (selected) => {
    setFees(selected)
  }

  const handleFeeDelete = (id) => {
    setFees((prev) => prev.filter((f) => f.id !== id))
  }

  // Deleting an adjustment: persisted records are kept but flagged _destroy so the
  // next save sends { id, _destroy } (Rails leaves un-mentioned records untouched,
  // so omitting them would NOT delete them). Never-saved records are just dropped.
  const handleAdjustmentDelete = (item, setList) => {
    setList((prev) => {
      if (isServerId(item.id)) {
        return prev.map((adjustment) => (adjustment.id === item.id ? { ...adjustment, _destroy: true } : adjustment))
      }
      return prev.filter((adjustment) => adjustment.id !== item.id)
    })
  }

  // Records flagged for deletion stay in state (to send _destroy on save) but are
  // hidden from the list.
  const visibleDayAdjustments = dayAdjustments.filter((adjustment) => !adjustment._destroy)
  const visibleSeasonalAdjustments = seasonalAdjustments.filter((adjustment) => !adjustment._destroy)

  if (!isTicketed) {
    return (
      <Flex vertical gap={spacings.M}>
        <GuestDetails form={form} />

        {isMultiDay ? (
          <MultiDayPricingModel form={form} />
        ) : (
          <PrivateBookingOptions form={form} />
        )}

        <Flex align="flex-start" gap={spacings.XS} className={styles.infoBox}>
          <InfoCircleOutlined className={styles.infoBoxIcon} />
          <Text className={styles.infoBoxText}>
            {t("experiences.pricing.adjustments_info")}
          </Text>
        </Flex>

        <AdjustmentsSection
          title={t("experiences.pricing.day_adjustments.title")}
          tooltip={t("experiences.pricing.day_adjustments.tooltip")}
          items={visibleDayAdjustments}
          onAdd={() => setIsDayModalOpen(true)}
          onEdit={handleDayEdit}
          onDelete={(item) => handleAdjustmentDelete(item, setDayAdjustments)}
          showAffects={false}
        />

        {/* Seasonal adjustments only apply to seasonal experiences; hide for All Year. */}
        {isSeasonal && (
          <AdjustmentsSection
            title={t("experiences.pricing.seasonal_adjustments.title")}
            tooltip={t("experiences.pricing.seasonal_adjustments.tooltip")}
            subtitle={t("experiences.pricing.seasonal_adjustments.subtitle")}
            items={visibleSeasonalAdjustments}
            onAdd={() => setIsSeasonalModalOpen(true)}
            onEdit={handleSeasonalEdit}
            onDelete={(item) => handleAdjustmentDelete(item, setSeasonalAdjustments)}
            showAffects={false}
          />
        )}

        <PaymentType />

        <TaxesAndFees
          fees={fees}
          onAdd={handleFeesAdd}
          onDelete={handleFeeDelete}
        />

        <AddPrivateSeasonalAdjustmentModal
          open={isSeasonalModalOpen}
          editingItem={editingSeasonalItem}
          defaultScheduleRanges={defaultScheduleRanges}
          onCancel={() => { setIsSeasonalModalOpen(false); setEditingSeasonalItem(null) }}
          onSave={handlePrivateSeasonalAdjustmentSave}
        />

        <AddDayAdjustmentModal
          open={isDayModalOpen}
          editingItem={editingDayItem}
          ticketTypes={[]}
          onCancel={() => { setIsDayModalOpen(false); setEditingDayItem(null) }}
          onSave={handleDayAdjustmentSave}
        />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={spacings.M}>
      <BasePrices />

      <Flex align="flex-start" gap={spacings.XS} className={styles.infoBox}>
        <InfoCircleOutlined className={styles.infoBoxIcon} />
        <Text className={styles.infoBoxText}>
          {t("experiences.pricing.adjustments_info")}
        </Text>
      </Flex>

      <AdjustmentsSection
        title={t("experiences.pricing.day_adjustments.title")}
        tooltip={t("experiences.pricing.day_adjustments.tooltip")}
        items={visibleDayAdjustments}
        onAdd={() => setIsDayModalOpen(true)}
        onEdit={handleDayEdit}
        onDelete={(item) => handleAdjustmentDelete(item, setDayAdjustments)}
      />

      <AdjustmentsSection
        title={t("experiences.pricing.seasonal_adjustments.title")}
        tooltip={t("experiences.pricing.seasonal_adjustments.tooltip")}
        subtitle={t("experiences.pricing.seasonal_adjustments.subtitle")}
        items={visibleSeasonalAdjustments}
        onAdd={() => setIsSeasonalModalOpen(true)}
        onEdit={handleSeasonalEdit}
        onDelete={(item) => handleAdjustmentDelete(item, setSeasonalAdjustments)}
      />

      <PaymentType />

      <TaxesAndFees
        fees={fees}
        onAdd={handleFeesAdd}
        onDelete={handleFeeDelete}
      />

      <AddDayAdjustmentModal
        open={isDayModalOpen}
        editingItem={editingDayItem}
        ticketTypes={ticketTypeOptions}
        onCancel={() => { setIsDayModalOpen(false); setEditingDayItem(null) }}
        onSave={handleDayAdjustmentSave}
      />

      <AddSeasonalAdjustmentModal
        open={isSeasonalModalOpen}
        editingItem={editingSeasonalItem}
        ticketTypes={ticketTypeOptions}
        onCancel={() => { setIsSeasonalModalOpen(false); setEditingSeasonalItem(null) }}
        onSave={handleSeasonalAdjustmentSave}
      />
    </Flex>
  )
}
